import React, { useState, useEffect } from "react";
import clsx from "clsx";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import { Avatar, Divider, Menu, MenuItem, Typography, Box } from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import {
	canSubmitProductFeedback,
	getInitials,
	useRouter,
	useIsBornAccount,
} from "utils";
import { isInImpersonationMode } from "frame/access-token";
import HelpFeedbackDialog from "../help-feedback-dialog";

const useStyles = makeStyles((theme) => ({
	root: {
		width: 300,
		maxWidth: "100%",
	},
	user: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "spaceBetween",
		alignItems: "center",
		padding: theme.spacing(3, 2),
		minWidth: 260,
	},
	avatarIcon: {
		position: "relative",
		paddingTop: 3,
		color: theme.palette.light,
		animation: "0.3s all ease-out",
		"&:hover": {
			color: theme.palette.white,
		},
	},
	userAvatar: {
		marginTop: theme.spacing(-2),
		width: 40,
		height: 40,
	},
	userInfo: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "flex-start",
		paddingLeft: 10,
	},
	error: {
		color: theme.palette.error.main,
	},
	success: {
		color: theme.palette.success.main,
	},
	onlineIndicator: {
		display: "flex",
		flex: 1,
		flexDirection: "row",
		justifyContent: "flex-start",
		alignItems: "center",
		marginBottom: theme.spacing(-5),
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(4),
	},
	onlineIndicatorIcon: {
		marginRight: "5px",
		width: "15px",
		height: "auto",
	},
	versionInfo: {
		padding: theme.spacing(1, 2),
		display: 'flex',
		justifyContent: 'center',
		color: theme.palette.text.secondary,
		fontSize: '0.75rem',
	},
}));

const resolveOrganisationId = (currentOrganisation) => {
	if (!currentOrganisation) {
		return null;
	}
	if (typeof currentOrganisation === "string") {
		return currentOrganisation;
	}
	return currentOrganisation.id || null;
};

const AccountPopover = ({
	anchor,
	open,
	profile,
	onClose,
	onLogout,
	onAccount,
	onExitImpersonationMode,
	hasNetwork,
	currentOrganisation,
}) => {
	const classes = useStyles();
	const { history } = useRouter();
	const isBornAccount = useIsBornAccount();
	const [helpOpen, setHelpOpen] = useState(false);
	const organisationId = resolveOrganisationId(currentOrganisation);
	const showHelp = canSubmitProductFeedback(profile?.role);

	// Use state to store version info
	const [versionInfo, setVersionInfo] = useState(() => {
		// First try to get version from localStorage
		const storedVersion = localStorage.getItem('app_version');
		// If no stored version, fall back to environment variable
		return storedVersion || process.env.REACT_APP_GIT_SHA || 'dev';
	});
	
	// Function to fetch the latest version information
	const refreshVersionInfo = () => {
		// Attempt to read from current environment first (most up-to-date)
		const envVersion = process.env.REACT_APP_GIT_SHA;
		
		// If available, update localStorage and state
		if (envVersion) {
			localStorage.setItem('app_version', envVersion);
			setVersionInfo(envVersion);
			return;
		}
		
		// Otherwise use stored version or fallback
		const storedVersion = localStorage.getItem('app_version');
		if (storedVersion) {
			setVersionInfo(storedVersion);
		} else {
			setVersionInfo('dev');
		}
	};
	
	// Keep version label in sync; do not auto-activate service workers here —
	// that raced with version-check / controllerchange and caused A/B reload loops.
	useEffect(() => {
		refreshVersionInfo();
	}, []);
	
	// Additional refresh when menu opens
	useEffect(() => {
		if (open) {
			refreshVersionInfo();
		}
	}, [open]);

	if (!anchor.current && !helpOpen) {
		return null;
	}

	const hasProfile = !!(profile && Object.keys(profile).length);
	const name = hasProfile
		? String(`${profile.firstName || ""} ${profile.lastName || ""}`).trim()
		: "";

	const isImpersonating = isInImpersonationMode();

	const onOpenHelp = () => {
		onClose();
		setHelpOpen(true);
	};

	return (
		<>
			{anchor.current && (
			<Menu
				id="menu-account"
				anchorEl={anchor.current}
				anchorOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				keepMounted
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				open={open}
				onClose={onClose}
			>
				<div className={classes.root}>
					{hasProfile && (
						<>
							<div className={classes.user}>
								{name ? (
									<Avatar
										alt="Person"
										className={classes.userAvatar}
										onClick={() =>
											isBornAccount
												? history.push("/secret")
												: null
										}
									>
										{getInitials(name)}
									</Avatar>
								) : (
									<Skeleton
										variant="circle"
										width={40}
										height={40}
									/>
								)}

								<div className={classes.userInfo}>
									{name ? (
										<>
											<Typography
												className={classes.name}
												variant="h6"
											>
												{isImpersonating
													? name + " (Impersonating)"
													: name}
											</Typography>
											<Typography variant="body2">
												{profile.email}
											</Typography>
											<Typography variant="body2">
												{profile.role}
											</Typography>
										</>
									) : (
										<>
											<Skeleton width={75} />
											<Skeleton width={145} />
										</>
									)}
									<div className={classes.onlineIndicator}>
										<FiberManualRecordIcon
											className={clsx(
												classes.onlineIndicatorIcon,
												hasNetwork
													? classes.success
													: classes.error
											)}
										/>
										<Typography variant="body2">
											{hasNetwork ? "ONLINE" : "OFFLINE"}
										</Typography>
									</div>
								</div>
							</div>
							<Divider />
						</>
					)}

					<MenuItem onClick={onAccount}>My account</MenuItem>
					{showHelp && (
						<MenuItem onClick={onOpenHelp} data-cy="help-feedback-btn">
							Feedback
						</MenuItem>
					)}
					{isImpersonating ? (
						<MenuItem onClick={onExitImpersonationMode}>
							Exit impersonation mode
						</MenuItem>
					) : (
						<MenuItem onClick={onLogout} data-cy={`logout-btn`}>
							Logout
						</MenuItem>
					)}
					<Divider />
					<Box className={classes.versionInfo}>
						Version: {versionInfo}
					</Box>
				</div>
			</Menu>
			)}
			{showHelp && (
				<HelpFeedbackDialog
					open={helpOpen}
					onClose={() => setHelpOpen(false)}
					organisationId={organisationId}
					appVersion={versionInfo}
				/>
			)}
		</>
	);
};

AccountPopover.propTypes = {
	anchor: PropTypes.object,
	open: PropTypes.bool,
	profile: PropTypes.shape({
		firstName: PropTypes.string,
		lastName: PropTypes.string,
		email: PropTypes.string,
		role: PropTypes.string,
	}).isRequired,
	onClose: PropTypes.func.isRequired,
	onLogout: PropTypes.func.isRequired,
	onAccount: PropTypes.func.isRequired,
	hasNetwork: PropTypes.bool.isRequired,
	currentOrganisation: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.object,
	]),
};

const mapStateToProps = ({ queue, organisations }) => ({
	hasNetwork: queue.hasNetwork,
	currentOrganisation: organisations?.currentOrganisation,
});

export default connect(mapStateToProps)(AccountPopover);
